// ─────────────────────────────────────────────────────────
// TEMA-SYSTEM — Design tokens, ThemeContext og ThemeProvider
// T er mutabel: Object.assign(T, theme) ved temaændring
// BG er let-binding: opdateres af apply_theme()
// ─────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { api } from './api';
export const DARK_THEME = {
 blaa: "#4f46e5",
 turkis: "#0d9488",
 creme: "#e2e8f0",
 lys: "#cbd5e1",
 dæmpet: "#b0bec5",
 subtil: "#64748b",
 moerk: "#f8fafc",
 glas: "rgba(255,255,255,0.05)",
 glas_kant: "rgba(255,255,255,0.06)",
 kort_kant: "2px solid rgba(13,148,136,0.38)",
 skillelinje: "2px solid rgba(13,148,136,0.38)",
 glas_hover: "rgba(255,255,255,0.95)",
 glas_aktiv: "rgba(79,70,229,0.06)",
 glas_lys: "rgba(255,255,255,0.03)",
 overflade: "#ffffff",
 roed: "#ef4444", gul: "#f59e0b", groen: "#10b981", lilla: "#8b5cf6", guld: "#b45309",
 grad_accent: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
 grad_indigo: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
 grad_guld: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
 grad_rose: "linear-gradient(135deg, #ef4444 0%, #f87171 100%)",
 grad_sky: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
 shadow: "0 1px 3px rgba(255,255,255,0.06), 0 4px 12px rgba(255,255,255,0.04)",
 shadow_md: "0 2px 8px rgba(255,255,255,0.06), 0 8px 24px rgba(255,255,255,0.06)",
 shadow_lg: "0 4px 12px rgba(0,0,0,0.05), 0 20px 60px rgba(255,255,255,0.08)",
 shadow_xl: "0 8px 24px rgba(255,255,255,0.06), 0 32px 80px rgba(0,0,0,0.1)",
 shadow_glow: "0 0 30px rgba(79,70,229,0.12)",
 shadow_teal: "0 4px 20px rgba(13,148,136,0.15)",
 r_sm: 10, r_md: 16, r_lg: 22, r_xl: 30,
 z_base: 0, z_kort: 1, z_sticky: 10, z_nav: 100, z_dropdown: 1000,
 z_modal: 5000, z_toast: 9000, z_dialog: 9500, z_session: 99999,
};
export const LIGHT_THEME = {
 ...DARK_THEME,
 blaa: "#4338ca",
 turkis: "#0d9488",
 // creme = primær tekst i lyst tema: #0f172a på #f8fafc → relLum 0.010/0.955 = 18.8:1 ✓ WCAG AAA
 creme: "#0f172a",
 // lys = sekundær tekst: #334155 på #f8fafc → relLum 0.050/0.955 = 9.8:1 ✓ WCAG AAA
 lys: "#334155",
 // dæmpet = dæmpet tekst (labels): #475569 på #f8fafc → relLum 0.076/0.955 = 5.6:1 ✓ WCAG AA (var 4.08:1, H-04 rettet)
 dæmpet: "#475569",
 // subtil = meget dæmpet: #64748b på #f8fafc → relLum 0.139/0.955 = 4.7:1 ✓ WCAG AA
 subtil: "#64748b",
 // moerk = KRITISK FIX: i mørkt tema er moerk lys (#f8fafc) til lys tekst på mørk bg
 // i lyst tema: moerk skal være MØRKt for tekst på lys baggrund — #1e293b → 13.4:1 ✓
 moerk: "#1e293b",
 glas: "rgba(255,255,255,0.85)",
 glas_kant: "rgba(15,23,42,0.08)",
 kort_kant: "1px solid rgba(13,148,136,0.25)",
 skillelinje: "1px solid rgba(15,23,42,0.08)",
 glas_hover: "rgba(255,255,255,1)",
 glas_aktiv: "rgba(13,148,136,0.08)",
 glas_lys: "rgba(255,255,255,0.6)",
 overflade: "#ffffff",
 roed: "#dc2626", gul: "#d97706", groen: "#059669", lilla: "#7c3aed", guld: "#92400e",
 shadow: "0 1px 3px rgba(15,23,42,0.06), 0 4px 12px rgba(15,23,42,0.04)",
 shadow_md: "0 2px 8px rgba(15,23,42,0.08), 0 8px 24px rgba(15,23,42,0.06)",
 shadow_lg: "0 4px 12px rgba(15,23,42,0.10), 0 20px 60px rgba(15,23,42,0.08)",
 shadow_xl: "0 8px 24px rgba(15,23,42,0.10), 0 32px 80px rgba(15,23,42,0.12)",
 shadow_glow: "0 0 30px rgba(67,56,202,0.18)",
 shadow_teal: "0 4px 20px rgba(13,148,136,0.20)",
};
// Mutable singleton — alle komponenter læser dette objekt direkte
export const T = { ...DARK_THEME };
export const DARK_BG = [
 "radial-gradient(ellipse 80% 50% at 70% 0%, rgba(35,34,190,0.06) 0%, transparent 70%)",
 "radial-gradient(ellipse 60% 40% at 20% 100%, rgba(63,212,199,0.04) 0%, transparent 60%)",
 "linear-gradient(180deg, #0a0e2e 0%, #080c14 40%, #060918 60%, #0a0e2e 100%)",
].join(", ");
export const LIGHT_BG = [
 "radial-gradient(ellipse 80% 50% at 70% 0%, rgba(35,34,190,0.04) 0%, transparent 70%)",
 "radial-gradient(ellipse 60% 40% at 20% 100%, rgba(13,148,136,0.05) 0%, transparent 60%)",
 "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 40%, #e2e8f0 60%, #f8fafc 100%)",
].join(", ");
export let BG = DARK_BG;
export function apply_theme(mode, calm_tech) {
 const theme = mode === 'light' ? LIGHT_THEME : DARK_THEME;
 const bg = mode === 'light' ? LIGHT_BG : DARK_BG;
 const root = document.documentElement;
 for (const k of Object.keys(theme)) {
 root.style.setProperty(`--sv-${k}`, String(theme[k]));
 }
 root.style.setProperty('--sv-bg', bg);
 root.setAttribute('data-theme', mode);
 root.setAttribute('data-calm', calm_tech ? 'on' : 'off');
 Object.assign(T, theme);
 BG = bg;
}
export const ThemeContext = React.createContext({
 theme_mode: 'dark', set_theme_mode: () => {},
 calm_tech: false, set_calm_tech: () => {},
 follows_os: true, set_follows_os: () => {},
});
// ── Globalt CSS injiceres én gang ved import ──
if (!document.getElementById("sv-premium-css")) {
 const s = document.createElement("style");
 s.id = "sv-premium-css";
 s.textContent = [
 "@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap')",
 "body { font-family:'Inter',system-ui,-apple-system,sans-serif !important; -webkit-font-smoothing:antialiased; margin:0; transition: background 0.4s ease, color 0.4s ease }",
 "html[data-theme='dark'] body { background:#080c14; color:#e2e8f0 }",
 "html[data-theme='light'] body { background:#f8fafc; color:#1e293b }",
 "html[data-calm='on'] .sv-main { filter: saturate(0.75) brightness(0.95) }",
 "html[data-calm='on'][data-theme='light'] .sv-main { filter: saturate(0.80) brightness(0.98) }",
 "@keyframes fadeUp { from { opacity:0; transform:translateY(16px) } to { opacity:1; transform:translateY(0) } }",
 "@keyframes fadeIn { from { opacity:0 } to { opacity:1 } }",
 "@keyframes shimmer { 0% { background-position:-200% 0 } 100% { background-position:200% 0 } }",
 "@keyframes pulse-glow { 0%,100% { box-shadow:0 0 0 0 rgba(79,70,229,0) } 50% { box-shadow:0 0 24px 6px rgba(79,70,229,0.08) } }",
 "@keyframes slide-in { from { opacity:0; transform:translateX(-20px) } to { opacity:1; transform:translateX(0) } }",
 "@keyframes spin { from { transform:rotate(0deg) } to { transform:rotate(360deg) } }",
 "@keyframes float { 0%,100% { transform:translateY(0) } 50% { transform:translateY(-4px) } }",
 "::-webkit-scrollbar { width:6px; height:6px }",
 "html[data-theme='dark'] ::-webkit-scrollbar-track { background:#1a1a2e }",
 "html[data-theme='dark'] ::-webkit-scrollbar-thumb { background:#334155; border-radius:10px }",
 "html[data-theme='dark'] ::-webkit-scrollbar-thumb:hover { background:#475569 }",
 "html[data-theme='light'] ::-webkit-scrollbar-track { background:#f1f5f9 }",
 "html[data-theme='light'] ::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:10px }",
 "html[data-theme='light'] ::-webkit-scrollbar-thumb:hover { background:#94a3b8 }",
 "input:focus, textarea:focus, select:focus { outline:none !important; border-color:rgba(79,70,229,0.4) !important; box-shadow:0 0 0 4px rgba(79,70,229,0.08) !important; }",
 "button { transition:all 0.2s cubic-bezier(0.4,0,0.2,1) !important; cursor:pointer }",
 "button:hover:not(:disabled) { transform:translateY(-1px) }",
 "button:active:not(:disabled) { transform:translateY(0) scale(0.98) }",
 "button:disabled { cursor:not-allowed; opacity:0.5 }",
 "* { scroll-behavior:smooth; box-sizing:border-box }",
 "html { -webkit-font-smoothing:antialiased }",
 "html[data-theme='dark'] ::selection { background:rgba(79,70,229,0.25); color:#ffffff }",
 "html[data-theme='light'] ::selection { background:rgba(79,70,229,0.15); color:#1e293b }",
 "html[data-theme='dark'] ::placeholder { color:#64748b !important }",
 "html[data-theme='light'] ::placeholder { color:#94a3b8 !important }",
 "html[data-theme='dark'] .sv-shimmer { background:linear-gradient(90deg, #1e293b 25%, #334155 50%, #1e293b 75%); background-size:200% 100%; animation:shimmer 1.5s ease infinite }",
 "html[data-theme='light'] .sv-shimmer { background:linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size:200% 100%; animation:shimmer 1.5s ease infinite }",
 "html[data-theme='dark'] { --sv-bottom-nav-bg:#080c1a; --sv-bottom-nav-border:rgba(255,255,255,0.06); --sv-bottom-nav-shadow:0 -4px 12px rgba(0,0,0,0.3) }",
 "html[data-theme='light'] { --sv-bottom-nav-bg:#ffffff; --sv-bottom-nav-border:rgba(15,23,42,0.08); --sv-bottom-nav-shadow:0 -4px 12px rgba(15,23,42,0.08) }",
 ".sv-toast { animation:fadeUp 0.3s cubic-bezier(0.34,1.56,0.64,1) }",
 /* ══ LYS-TEMA STRUKTURELLE RETTELSER (K-01..K-05, H-01..H-07) ══ */
 /* K-01: Dropdown-tekst — select-elementer med colorScheme:dark giver hvid tekst på lys bg */
 "html[data-theme='light'] select { color-scheme: light !important; background:#ffffff !important; color:#1e293b !important; border-color:rgba(15,23,42,0.15) !important }",
 "html[data-theme='light'] select option { background:#ffffff; color:#1e293b }",
 /* K-02: Filter-pills René/Daisy/Primær — pillerne bruger rgba(255,255,255,0.x) borders → usynlige */
 "html[data-theme='light'] .sv-liste-row { background:transparent !important }",
 "html[data-theme='light'] .sv-liste-row:hover { background:rgba(15,23,42,0.03) !important }",
 /* K-03: Input-felter i lyst tema — fjern mørk colorScheme, sæt lys baggrund */
 /* !important overskriver inline styles fra komponenter */
 "html[data-theme='light'] input, html[data-theme='light'] textarea, html[data-theme='light'] .sv-input { color-scheme: light !important; background:#ffffff !important; color:#0f172a !important; border-color:rgba(15,23,42,0.15) !important }",
 "html[data-theme='light'] input::placeholder, html[data-theme='light'] textarea::placeholder, html[data-theme='light'] .sv-input::placeholder { color:#94a3b8 !important }",
 /* K-04: Topbar log-ud knap og tema-knap — hvid ikonalfabet på lys topbar */
 "html[data-theme='light'] .sv-topbar { background:linear-gradient(180deg,rgba(248,250,252,0.96) 0%,rgba(241,245,249,0.93) 100%) !important; border-bottom-color:rgba(15,23,42,0.08) !important; box-shadow:0 1px 0 rgba(15,23,42,0.05),0 4px 20px rgba(15,23,42,0.06) !important }",
 "html[data-theme='light'] .sv-topbar button { color:#1e293b !important }",
 "html[data-theme='light'] .sv-topbar-logout { color:#475569 !important; background:rgba(15,23,42,0.04) !important; border-color:rgba(15,23,42,0.12) !important }",
 "html[data-theme='light'] .sv-topbar-logout:hover { color:#1e293b !important; background:rgba(15,23,42,0.08) !important }",
 /* K-04: ThemeToggle-knap i topbar */
 "html[data-theme='light'] .sv-topbar-theme button { background:rgba(15,23,42,0.04) !important; border-color:rgba(15,23,42,0.10) !important; color:#334155 !important }",
 /* K-05: Wizard CPR-felt og andre input i wizard — mørk baggrund i ellers lys formular */
 "html[data-theme='light'] .sv-wizard input[type='text'], html[data-theme='light'] .sv-wizard input[type='date'], html[data-theme='light'] .sv-wizard input[type='email'], html[data-theme='light'] .sv-wizard input[type='tel'], html[data-theme='light'] .sv-wizard select { background:#ffffff !important; color:#0f172a !important; border-color:rgba(15,23,42,0.18) !important }",
 /* H-01: Sidebar skal matche lyst tema — mørk sidebar mod lys side */
 "html[data-theme='light'] .sv-sidebar-desktop { background:linear-gradient(180deg,#1e293b 0%,#0f172a 100%) !important }",
 /* H-04: Labels (dæmpet) — nu 5.6:1 via LIGHT_THEME.dæmpet=#475569. Sekundære navne i liste */
 "html[data-theme='light'] .sv-liste-sagsnr { color:#475569 !important }",
 /* H-06: Bundnav i lyst tema — hvid bundnav passer med lyst tema, mørk topbar bryder */
 "html[data-theme='light'] .sv-bottom-nav { background:rgba(248,250,252,0.97) !important; border-top-color:rgba(15,23,42,0.08) !important }",
 "html[data-theme='light'] .sv-bottom-nav span { color:#334155 !important }",
 "html[data-theme='light'] .sv-bottom-nav span.aktiv { color:#0d9488 !important }",
 /* H-07: Wizard step-labels — 9px → 13px */
 "html[data-theme='light'] .sv-wizard-step-label, html[data-theme='dark'] .sv-wizard-step-label { font-size:13px !important; min-width:56px !important }",
 /* Login-undertitel: rgba(255,255,255,0.5) → 2.2:1 i lyst — fix til mørk, læsbar */
 "html[data-theme='light'] .sv-login-undertitel { color:#475569 !important }",
 /* Log-ind knap 40% opacity → fuld opacity i lyst tema */
 "html[data-theme='light'] .sv-login-knap:disabled { opacity:0.65 !important }",
 /* Søge-eksempeltekst placeholder */
 "html[data-theme='light'] .sv-soeg-input::placeholder { color:#94a3b8 !important }",
 /* AI-FAB: rød brand-mismatch → brandfarve turkis */
 "html[data-theme='light'] .sv-ai-fab, html[data-theme='dark'] .sv-ai-fab { background:linear-gradient(135deg,#0d9488 0%,#14b8a6 100%) !important; border-color:rgba(13,148,136,0.3) !important }",
 /* Graf Y-akse labels synlige i lyst tema */
 "html[data-theme='light'] .recharts-cartesian-axis-tick text { fill:#475569 !important }",
 "html[data-theme='light'] .recharts-cartesian-axis-line { stroke:rgba(15,23,42,0.12) !important }",
 /* Filterbar i liste */
 "html[data-theme='light'] .sv-liste-filterbar { background:rgba(248,250,252,0.95) !important }",
 /* Nyligt-set sektion */
 "html[data-theme='light'] .sv-nyligt-set-badge { color:#1e293b !important; border-color:rgba(15,23,42,0.15) !important; background:rgba(15,23,42,0.04) !important }",
 /* ══ MØRKT-TEMA RETTELSER (K1-K7, H1-H5, M1/M5) ══ */
 /* K2: Login-inputs — sikr lys tekst i mørkt tema; lys-tema K-03 override forbliver uberørt */
 "html[data-theme='dark'] input.sv-input, html[data-theme='dark'] textarea.sv-input { color:#e2e8f0 !important; background:rgba(8,12,30,0.65) !important; border-color:rgba(255,255,255,0.18) !important }",
 /* K3: 'Alle roller' aktiv filter — blå tekst på blå transparent bg → ~1.00:1; hvid tekst + stærkere bg */
 /* #ffffff (relLum=1) på rgba(59,130,246,0.55)≈#1d3a7a (relLum≈0.038) → (1+0.05)/(0.038+0.05)=11.9:1 ✓ */
 "html[data-theme='dark'] .sv-dk-rolle-filter-aktiv { color:#ffffff !important; background:rgba(59,130,246,0.55) !important; border-color:rgba(96,165,250,0.7) !important }",
 /* K4: Grøn 'Alle' sagsbehandler-filter aktiv — T.turkis (#0d9488) på rgba(63,212,199,0.12)≈#0e1a19 → ~2.04:1 */
 /* #ffffff på rgba(13,148,136,0.55)≈#1a6e6b (relLum≈0.046) → (1+0.05)/(0.046+0.05)=10.9:1 ✓ */
 "html[data-theme='dark'] .sv-dk-sb-filter-aktiv { color:#ffffff !important; background:rgba(13,148,136,0.55) !important; border-color:rgba(13,148,136,0.75) !important }",
 /* K5: Profil-avatar border — rgba(63,212,199,0.25) → ~1.49:1 visuelt; hæv til 0.60 for synlig kant */
 "html[data-theme='dark'] .sv-dk-profil-avatar { border-color:rgba(63,212,199,0.60) !important }",
 /* K6: Topbar dato-undertitel — rgba(255,255,255,0.4)→~2.3:1; 0.72→~4.5:1 */
 /* #ffffff*0.72 blended on #080c14: effektiv lum ≈ 0.72; (0.72+0.05)/(0.002+0.05)=14.8:1 ✓ */
 "html[data-theme='dark'] .sv-topbar-subtitle { color:rgba(255,255,255,0.72) !important }",
 /* K7: Logout-ikon — rgba(255,255,255,0.4)→~2.3:1; 0.72→~4.5:1 ✓ */
 "html[data-theme='dark'] .sv-topbar-logout { color:rgba(255,255,255,0.72) !important }",
 /* H3: Aktiv sidebar-item bg — rgba(63,212,199,0.18) på #080c14 → ~2.0:1 (non-tekst, kræver ≥3:1) */
 /* Hæv opacity → rgba(63,212,199,0.28) + stærkere glow for klar visuel distinktion */
 /* Effektiv bg rgba(63,212,199,0.28) blended: ~rgb(21,50,47) relLum≈0.008 vs #080c14≈0.002 → 3.6:1 ✓ */
 "html[data-theme='dark'] .sv-sidebar-aktiv-bg { background:linear-gradient(135deg,rgba(63,212,199,0.28) 0%,rgba(79,70,229,0.18) 100%) !important; border-color:rgba(63,212,199,0.40) !important }",
 /* H1: Inaktive filterknapper (Dashboard + Sidebar) — 1.91:1 + 12px; tilsæt klasse via CSS */
 /* Dashboard filterbar inaktive: #b0bec5 på rgba(255,255,255,0.04)≈#0d1018 → (0.511+0.05)/(0.002+0.05)=10.8:1 ✓ */
 /* Font-størrelse er dog 12px — hæv til 13px */
 "html[data-theme='dark'] .sv-liste-filterbar button { font-size:13px !important }",
 /* H2: 'Alle sager'-knap — font-size 12px; hæv til 13px */
 "html[data-theme='dark'] .sv-dk-alle-sager-knap { font-size:13px !important }",
 /* H4: 'Se alle →' link — font-size 11px; hæv til 12px minimum */
 "html[data-theme='dark'] .sv-dk-se-alle { font-size:12px !important }",
 /* H5: Graf-aksetekst fill:#64748b hardkodet */
 /* #a8b3c2 (relLum≈0.203) på #080c14 (relLum≈0.002) → (0.203+0.05)/(0.002+0.05)=4.87:1 ✓ AA */
 "html[data-theme='dark'] .recharts-cartesian-axis-tick text { fill:#a8b3c2 !important }",
 /* M1: KPI-kort borders — farve*0.08 opacity (8%); hæv synlighed via box-shadow ring */
 /* Kan ikke override dynamisk hex+opacity; tilføj subtil hvid ring som supplement */
 "html[data-theme='dark'] .sv-kpi-kort { box-shadow:0 4px 24px rgba(0,0,0,0.25),inset 0 1px 0 rgba(255,255,255,0.07),0 0 0 1px rgba(255,255,255,0.06) !important }",
 /* M5: Input-borders — hæv fra ~7% til 18% opacity i mørkt tema */
 "html[data-theme='dark'] input:not(.sv-input), html[data-theme='dark'] textarea:not(.sv-input) { border-color:rgba(255,255,255,0.18) !important }",
 /* ══ V1.6.73 — 4 REST-RETTELSER (design_ux Android-audit) ══ */
 /* R1: Wizard-inputs mørkt tema — rgba(255,255,255,0.04) → solid mørk bg + lys tekst */
 /* #e2e8f0 (relLum=0.711) på rgb(20,28,48) (relLum≈0.012) → (0.711+0.05)/(0.012+0.05)=12.3:1 ✓ WCAG AAA */
 /* Rammer ALLE input/textarea i .sv-wizard (ikke kun CPR) — K-05 dækkede kun lyst tema */
 "html[data-theme='dark'] .sv-wizard input, html[data-theme='dark'] .sv-wizard textarea { background:rgb(20,28,48) !important; color:#e2e8f0 !important; border-color:rgba(255,255,255,0.18) !important }",
 /* R2: Avatar-initialer lyst tema — #3fd4c7 (relLum=0.566) på rgba(63,212,199,0.15)/#f8fafc=rgb(220,244,244) → 1.51:1 */
 /* Fix: Mørk tekst #0f172a (relLum=0.010) på rgba(63,212,199,0.15)/#f8fafc → (0.955+0.05)/(0.010+0.05)=16.8:1 ✓ */
 /* Mørkt tema beholdes: #3fd4c7 på rgba(63,212,199,0.15)/#080c14 → 8.6:1 ✓ — ingen override nødvendig */
 "html[data-theme='light'] .sv-dk-profil-avatar .sv-dk-profil-initialer { color:#0f172a !important }",
 "html[data-theme='light'] .sv-dk-profil-avatar .sv-dk-profil-label { color:#1e293b !important }",
 /* Solid lys-turkis baggrund i lyst tema — giver cirklen visuel tydelighed og bevarar kontrastforhold */
 "html[data-theme='light'] .sv-dk-profil-avatar { background:rgba(13,148,136,0.18) !important; border-color:rgba(13,148,136,0.40) !important }",
 /* R3: Aktiv wizard-fane tekst — lyst tema: hardkodet #3fd4c7 (relLum=0.566) på #f8fafc (relLum=0.955) → 1.63:1 */
 /* v1.6.73-fix: #0d9488 (relLum=0.230) → FAKTISK 3.58:1 (under WCAG AA) — beregningsfejl i v1.6.73 */
 /* v1.6.74-fix: #115e59 teal-800 (relLum=0.088) → (0.954+0.05)/(0.088+0.05)=7.25:1 ✓ WCAG AA+margin */
 /* Mørkt tema #3fd4c7 på #080c14 → 9.9:1 ✓ — beholdes */
 "html[data-theme='light'] .sv-wizard-step-aktiv { color:#115e59 !important; border-bottom-color:#115e59 !important; box-shadow:none !important }",
 /* R3b: Topbar sidetitel lyst tema — hardkodet color:#e2e8f0 (relLum=0.711) på lys topbar #f8fafc (relLum=0.954) → 1.18:1 — usynlig */
 /* Fix: #1e293b (relLum=0.022) → (0.954+0.05)/(0.022+0.05)=13.98:1 ✓ WCAG AAA */
 /* Mørkt tema: farven er sat inline i App.js color:#e2e8f0 — beholdes */
 "html[data-theme='light'] .sv-topbar-page-title { color:rgb(30,41,59) !important }",
 /* R3c: Inaktive wizard-faner lyst tema — T.dæmpet evalueret ved module-load = #b0bec5 (mørkt tema) */
 /* #b0bec5 (relLum=0.501) på #f8fafc (relLum=0.954) → 1.82:1 — WCAG fejl */
 /* Fix: #475569 slate-600 (relLum=0.089) → (0.954+0.05)/(0.089+0.05)=7.24:1 ✓ WCAG AA */
 /* Aktiv fane (#115e59, 7.25:1) forbliver mørkere teal end inaktiv (#475569, neutral) → visuel hierarki bevaret */
 /* Mørkt tema: T.dæmpet=#b0bec5 på #080c14 → 10.8:1 ✓ — ingen override */
 "html[data-theme='light'] .sv-wizard button:not(.sv-wizard-step-aktiv) { color:#475569 !important }",
 /* R4: Ghost-knap Annuller — KNAP_VARIANTER.ghost.color = T.dæmpet evalueret ved module-load (mørkt=#b0bec5) */
 /* #b0bec5 (relLum=0.511) på lys bg #f8fafc (relLum=0.955) → 1.79:1 — WCAG fejl */
 /* Fix lyst tema: #475569 (relLum=0.076) → (0.955+0.05)/(0.076+0.05)=7.97:1 ✓ WCAG AA */
 /* Mørkt tema: #b0bec5 på #080c14 → 10.8:1 ✓ — beholdes */
 "html[data-theme='light'] button.sv-knap-ghost { color:#475569 !important; border-color:rgba(15,23,42,0.15) !important }",
 "html[data-theme='light'] button.sv-knap-ghost:hover:not(:disabled) { color:#1e293b !important; background:rgba(15,23,42,0.04) !important }",
 ].join("\n");
 document.head.appendChild(s);
}
export function ThemeProvider({ children }) {
 const initial_mode = () => {
 try {
 const saved = localStorage.getItem("sv_theme_mode");
 if (saved === 'dark' || saved === 'light') return saved;
 } catch(_) {}
 if (typeof window !== 'undefined' && window.matchMedia) {
 return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
 }
 return 'dark';
 };
 const [theme_mode, set_theme_mode_state] = useState(initial_mode);
 const [calm_tech, set_calm_tech_state] = useState(() => {
 try { return localStorage.getItem("sv_calm_tech") === "1"; } catch(_) { return false; }
 });
 const [follows_os, set_follows_os_state] = useState(() => {
 try { return localStorage.getItem("sv_theme_mode") === null; } catch(_) { return true; }
 });
 // force_update fjernet: apply_theme sætter CSS custom properties på :root
 // udenfor React-cyklus — React-state-ændringer på theme_mode/calm_tech
 // trigger allerede re-render via set_theme_mode_state/set_calm_tech_state,
 // så den ekstra useState(0)-re-render var redundant og gav rest-flash ved login.
 useEffect(() => {
 apply_theme(theme_mode, calm_tech);
 }, [theme_mode, calm_tech]);
 // RENE-1/#18: Hent kun bruger-præferencer hvis brugeren er logget ind.
 // ThemeProvider kører FØR login-guard i AppIndre — uden denne check ville
 // et 401-svar fra api() trigge window.location.reload() og skabe en
 // uendelig reload-loop på login-siden.
 useEffect(() => {
 let afbrudt = false;
 const erLoggetInd = (() => {
   try { return sessionStorage.getItem('sv_bruger') !== null; } catch(_) { return false; }
 })();
 if (!erLoggetInd) return; // Skip profil-hentning når ikke logget ind
 (async () => {
 try {
 const res = await api("brugere/profil", "GET");
 if (afbrudt || !res?.bruger?.indstillinger) return;
 const i = res.bruger.indstillinger;
 if (i.theme_mode === 'dark' || i.theme_mode === 'light') {
 set_theme_mode_state(i.theme_mode);
 set_follows_os_state(false);
 localStorage.setItem("sv_theme_mode", i.theme_mode);
 }
 if (typeof i.calm_tech === 'boolean') {
 set_calm_tech_state(i.calm_tech);
 localStorage.setItem("sv_calm_tech", i.calm_tech ? "1" : "0");
 }
 } catch(_) {}
 })();
 return () => { afbrudt = true; };
 }, []);
 useEffect(() => {
 if (!follows_os || typeof window === 'undefined' || !window.matchMedia) return;
 const mq = window.matchMedia('(prefers-color-scheme: light)');
 const handler = e => set_theme_mode_state(e.matches ? 'light' : 'dark');
 try { mq.addEventListener('change', handler); } catch(_) { mq.addListener && mq.addListener(handler); }
 return () => {
 try { mq.removeEventListener('change', handler); } catch(_) { mq.removeListener && mq.removeListener(handler); }
 };
 }, [follows_os]);
 const gem_praeference = (patch) => {
 api("brugere/profil", "PATCH", { indstillinger: patch }).catch(() => {});
 };
 const set_theme_mode = (mode) => {
 set_theme_mode_state(mode);
 set_follows_os_state(false);
 try { localStorage.setItem("sv_theme_mode", mode); } catch(_) {}
 gem_praeference({ theme_mode: mode, calm_tech });
 };
 const set_calm_tech = (on) => {
 set_calm_tech_state(on);
 try { localStorage.setItem("sv_calm_tech", on ? "1" : "0"); } catch(_) {}
 gem_praeference({ theme_mode, calm_tech: on });
 };
 const set_follows_os = (on) => {
 set_follows_os_state(on);
 if (on) {
 try { localStorage.removeItem("sv_theme_mode"); } catch(_) {}
 if (window.matchMedia) {
 const os_mode = window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
 set_theme_mode_state(os_mode);
 }
 }
 };
 return (
 <ThemeContext.Provider value={{ theme_mode, set_theme_mode, calm_tech, set_calm_tech, follows_os, set_follows_os }}>
 {children}
 </ThemeContext.Provider>
 );
}
export function ThemeToggle({ style }) {
 const ctx = React.useContext(ThemeContext);
 const [aabent, setAabent] = useState(false);
 const ikon = ctx.theme_mode === 'light' ? '' : '';
 return (
 <div style={{ position:"relative", ...style }}> <button
 onClick={() => setAabent(v => !v)}
 aria-label={`Tema: ${ctx.theme_mode === 'light' ? 'Lyst' : ctx.follows_os ? 'Følger system' : 'Mørkt'}${ctx.calm_tech ? ', Calm-Tech' : ''} — klik for at ændre`}
 aria-expanded={aabent}
 aria-haspopup="true"
 title={`Aktuelt: ${ctx.theme_mode}${ctx.calm_tech ? " · Calm" : ""}`}
 style={{
 background: "rgba(255,255,255,0.04)",
 border: "1px solid rgba(255,255,255,0.08)",
 borderRadius: 10, padding: "8px 10px", color: T.creme,
 fontSize: 15, cursor: "pointer", lineHeight: 1,
 }}>
 {ikon}
 </button>
 {aabent && (
 <> <div onClick={() => setAabent(false)} style={{ position:"fixed", inset:0, zIndex: T.z_dropdown - 1 }} /> <div style={{
 position:"absolute", top:"calc(100% + 8px)", right:0, minWidth:220,
 background: ctx.theme_mode === 'light' ? "#ffffff" : "#0f1328",
 border: `1px solid ${ctx.theme_mode === 'light' ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.1)"}`,
 borderRadius: 12, padding: 10, zIndex: T.z_dropdown,
 boxShadow: T.shadow_lg,
 }}> <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: T.dæmpet, padding: "4px 8px 8px" }}>Tema</div>
 {[['dark',' Mørkt'],['light',' Lyst']].map(([v,l]) => (
 <button key={v} onClick={() => { ctx.set_theme_mode(v); setAabent(false); }}
 style={{
 display:"block", width:"100%", textAlign:"left", padding:"8px 10px", marginBottom: 2,
 background: ctx.theme_mode === v && !ctx.follows_os ? `${T.turkis}22` : "transparent",
 border:"none", borderRadius: 8, color: T.creme, fontSize: 13, cursor:"pointer",
 }}>
 {l}{ctx.theme_mode === v && !ctx.follows_os ? " " : ""}
 </button>
 ))}
 <button onClick={() => { ctx.set_follows_os(true); setAabent(false); }}
 style={{
 display:"block", width:"100%", textAlign:"left", padding:"8px 10px", marginBottom: 2,
 background: ctx.follows_os ? `${T.turkis}22` : "transparent",
 border:"none", borderRadius: 8, color: T.creme, fontSize: 13, cursor:"pointer",
 }}>
 Følg systemet{ctx.follows_os ? " " : ""}
 </button> <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "8px 0" }} /> <label style={{ display:"flex", alignItems:"center", gap: 8, padding: "8px 10px", cursor: "pointer", fontSize: 13, color: T.creme }}> <input type="checkbox" checked={ctx.calm_tech} onChange={e => ctx.set_calm_tech(e.target.checked)} style={{ cursor:"pointer" }} /> <span> Calm-Tech Mode</span> </label> <div style={{ fontSize: 11, color: T.dæmpet, padding: "4px 10px 6px", lineHeight: 1.4 }}>
 Dæmper farver og lys — beregnet til tunge sager og sen aften.
 </div> </div> </>
 )}
 </div>
 );
}